import React, { useEffect, useState } from "react";
import { track } from "../utils/analytics";
import { FaDiscord, FaApple, FaGooglePlay } from "react-icons/fa";
import { NextSeo } from "next-seo";
import Image from "next/image";
import { Button } from "@jammable/ui-core";

const LANG_PARAM_TO_LOCALE = {
  en: "en",
  fr: "fr",
  pt: "pt",
  es: "es",
  ru: "ru",
  sk: "sk",
};

const LOCALE_STRINGS = {
  en: {
    seoTitle: "Jammable App – Join Discord & Download",
    heading: "Get the Jammable App",
    subheading:
      "Make mashups in seconds. Join our community or grab the app to start creating.",
    discord: "Join Discord",
    appstore: "App Store",
    playstore: "Play Store",
    getItOn: "Get it on",
    members: "members",
  },
  fr: {
    seoTitle: "Application Jammable – Rejoindre Discord & Télécharger",
    heading: "Télécharge l'app Jammable",
    subheading:
      "Crée des mashups en quelques secondes. Rejoins notre communauté ou télécharge l'app pour commencer à créer.",
    discord: "Rejoindre Discord",
    appstore: "App Store",
    playstore: "Play Store",
    getItOn: "Disponible sur",
    members: "membres",
  },
  pt: {
    seoTitle: "App Jammable – Entrar no Discord e Baixar",
    heading: "Baixe o app Jammable",
    subheading:
      "Crie mashups em segundos. Junte-se à comunidade ou baixe o app para começar a criar.",
    discord: "Entrar no Discord",
    appstore: "App Store",
    playstore: "Play Store",
    getItOn: "Disponível em",
    members: "membros",
  },
  es: {
    seoTitle: "Aplicación Jammable – Únete a Discord y Descarga",
    heading: "Descarga la app de Jammable",
    subheading:
      "Crea mashups en segundos. Únete a la comunidad o descarga la app para empezar a crear.",
    discord: "Unirse a Discord",
    appstore: "App Store",
    playstore: "Play Store",
    getItOn: "Disponible en",
    members: "miembros",
  },
  ru: {
    seoTitle: "Приложение Jammable – Присоединиться к Discord и Скачать",
    heading: "Скачайте приложение Jammable",
    subheading:
      "Создавайте мэшапы за секунды. Присоединяйтесь к нашему сообществу или скачайте приложение, чтобы начать творить.",
    discord: "Присоединиться к Discord",
    appstore: "App Store",
    playstore: "Play Store",
    getItOn: "Доступно в",
    members: "участников",
  },
  sk: {
    seoTitle: "Aplikácia Jammable – Pridaj sa na Discord a Stiahni",
    heading: "Stiahni si aplikáciu Jammable",
    subheading:
      "Vytváraj mashupy za pár sekúnd. Pridaj sa do našej komunity alebo si stiahni aplikáciu a začni tvoriť.",
    discord: "Pridaj sa na Discord",
    appstore: "App Store",
    playstore: "Play Store",
    getItOn: "Dostupné na",
    members: "členov",
  },
};

const AppPage = () => {
  const [source, setSource] = useState(null);
  const [explicitLocale, setExplicitLocale] = useState(null);

  console.log(source);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const src =
      params.get("source") ||
      params.get("utm_source") ||
      params.get("utm_medium") ||
      params.get("utm_campaign") ||
      params.get("utm_content") ||
      params.get("from") ||
      params.get("ref") ||
      params.get("q");

    const langParam =
      params.get("lang") ||
      params.get("locale") ||
      params.get("language") ||
      params.get("hl");

    setSource(src);

    if (langParam) {
      const key = langParam.toLowerCase();
      const resolved = LANG_PARAM_TO_LOCALE[key];
      if (resolved) setExplicitLocale(resolved);
    }
  }, []);

  const locale = explicitLocale || "en";
  const t = (key) =>
    (LOCALE_STRINGS[locale] && LOCALE_STRINGS[locale][key]) ||
    LOCALE_STRINGS.en[key];

  const handleDiscordClick = async () => {
    await track("discord_invite_click", {
      source: source,
      location: "app_page",
      language: locale,
    });
    window.open("https://discord.gg/jammable", "_blank");
  };

  const handleAppStoreClick = async () => {
    await track("app_store_click", {
      source,
      location: "app_page",
      language: locale,
    });
    window.location.href = "/download";
  };

  const handlePlayStoreClick = async () => {
    await track("play_store_click", {
      source,
      location: "app_page",
      language: locale,
    });
    window.open(
      "https://play.google.com/store/apps/details?id=com.jammable.app",
      "_blank",
    );
  };

  return (
    <div
      lang={locale}
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0f0f12 0%, #1a1a1a 50%, #0f0f12 100%)",
        color: "white",
        padding: "24px",
      }}
    >
      <NextSeo title={t("seoTitle")} />

      <div
        style={{
          maxWidth: 720,
          width: "100%",
          textAlign: "center",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Image
          src="https://images.jammable.com/MashupLogo.png"
          alt="Jammable Logo"
          style={{
            display: "block",
            margin: "0 auto 24px",
            width: "180px",
            maxWidth: "60%",
            filter: "drop-shadow(0 0 20px rgba(0,0,0,0.35))",
          }}
          width={180}
          height={180}
          className="tw-rounded-4xl tw-shadow-[0_0_40px_rgba(255,255,255,0.5)]"
        />
        <h1 className="tw-text-4xl md:tw-text-6xl tw-font-black tw-leading-tight tw-mb-3">
          {t("heading")}
        </h1>
        <div className="tw-text-white/70 tw-text-base md:tw-text-lg tw-mb-8">
          {t("subheading")}
        </div>

        <div className="tw-flex tw-flex-col tw-gap-2 tw-items-center tw-justify-center tw-w-[300px]">
          <Button
            onClick={handleAppStoreClick}
            className="tw-uppercase tw-flex tw-font-black tw-items-center tw-justify-center tw-rounded-2xl tw-bg-white tw-text-black hover:tw-opacity-90 tw-transition tw-duration-150 tw-flex tw-gap-1 md:tw-px-6 md:tw-pr-8 tw-h-14 tw-py-0 tw-border-none hover:tw-bg-white tw-w-full"
          >
            <FaApple size={30} />

            <div className="tw-flex tw-flex-col tw-items-start tw-justify-center">
              <div className="tw-text-xs tw-font-semibold tw--mb-0.5">
                {t("getItOn")}
              </div>
              <div className="tw-flex tw-gap-1 tw-text-base tw-items-center tw-justify-center tw-font-black">
                {t("appstore")}
              </div>
            </div>
          </Button>

          <Button
            onClick={handlePlayStoreClick}
            className="tw-uppercase tw-font-black tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[#34A853] tw-text-white hover:tw-bg-[#2C8C46] tw-transition tw-duration-150 tw-flex tw-gap-2 tw-px-6 tw-h-14 tw-border-none tw-w-full"
          >
            <FaGooglePlay size={30} color="#ffffff" />
            <div className="tw-flex tw-flex-col tw-items-start tw-justify-center">
              <div className="tw-text-xs tw-font-semibold tw--mb-0.5">
                {t("getItOn")}
              </div>
              <div className="tw-flex tw-gap-1 tw-text-base tw-items-center tw-justify-center tw-font-black">
                {t("playstore")}
              </div>
            </div>
          </Button>
          <Button
            onClick={handleDiscordClick}
            className="tw-uppercase  tw-border-none tw-font-black tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[#5865F2] hover:tw-bg-[#4752C4] tw-transition tw-duration-150 tw-flex tw-gap-2 tw-px-6 tw-h-14 tw-w-full"
          >
            <FaDiscord size={30} />
            <div className="tw-flex tw-flex-col tw-items-start tw-justify-center">
              <div className="tw-text-xs tw-font-semibold tw--mb-0.5">
                10K+ {t("members")}
              </div>
              <div className="tw-flex tw-gap-1 tw-text-base tw-items-center tw-justify-center tw-font-black">
                {t("discord")}
              </div>
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppPage;

export const getServerSideProps = async (context) => {
  const { req } = context;
  const source = req.headers["x-forwarded-for"] || req.headers["referer"];
  return {
    props: { source },
  };
};
